import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ExerciseType } from '@org/model';
import { cn } from '../../../../utils';
import { AlertCircle, CheckCircle2, Link2 } from 'lucide-react';
import type { ContentDisplayVariant } from '../../hooks/content-renderer-types';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import type {
  ExerciseFeedbackMessage,
  ExerciseSubmitHandler,
} from '../../hooks/content-renderer-types';
import { ContentDescription } from '../shared/content-description';
import { renderInlineMarkdown } from '../../../custom/markdown';
import { WorkbookPanel, ZineButton } from '../../zine';
import { zineColors, zineFonts, zinePairColorFor, type ZinePairColor } from '../../zine/zine-theme';
import {
  splitWorkbookFeedback,
  WorkbookAttemptText,
  WorkbookExtraFeedback,
  WorkbookVerdict,
} from './zine-workbook';

interface MatchingExerciseProps {
  content: StudentExerciseContent;
  onExerciseSubmit?: ExerciseSubmitHandler;
  feedback?: ExerciseFeedbackMessage[];
  variant?: ContentDisplayVariant;
  isReadOnly?: boolean;
}

interface MatchingPair {
  pairId: string;
  promptId: string;
  promptLabel: string;
  optionId: string;
  optionLabel: string;
  order: number;
}

interface MatchState {
  promptId: string;
  optionId: string;
  colorIndex: number;
}

/** Seeded Fisher-Yates shuffle for deterministic option ordering. */
function seededShuffle<T>(items: T[], seed: number): T[] {
  let state = seed;
  const random = () => {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

interface CardProps {
  id: string;
  label: string;
  isSelected: boolean;
  isMatched: boolean;
  matchNumber: number | null;
  pairColor: ZinePairColor | null;
  evaluation?: {
    isCorrect: boolean;
    grade?: 'perfect' | 'near' | 'partial' | 'wrong';
  } | null;
  isReadOnly: boolean;
  onClick: () => void;
  type: 'prompt' | 'option';
}

function MatchCard({
  id,
  label,
  isSelected,
  isMatched,
  matchNumber,
  pairColor,
  evaluation,
  isReadOnly,
  onClick,
  type,
}: CardProps) {
  const grade = evaluation
    ? evaluation.grade ?? (evaluation.isCorrect ? 'perfect' : 'wrong')
    : null;

  // Zine card states: default (white/line), selected (charm ring), matched
  // (the pair's spot-ink color, shared by both cards of the pair so students
  // can see the correspondence), graded correct (good) and graded wrong (red).
  const cardStyle =
    grade === 'perfect'
      ? { background: zineColors.goodSoft, borderColor: zineColors.good, color: zineColors.good }
      : grade === 'wrong'
        ? { background: zineColors.baby, borderColor: zineColors.redApplied, color: zineColors.redApplied }
        : isMatched && pairColor
          ? { background: pairColor.soft, borderColor: pairColor.solid, color: zineColors.ink }
          : isSelected
            ? { background: '#fff', borderColor: zineColors.charm, color: zineColors.ink }
            : { background: '#fff', borderColor: zineColors.line, color: zineColors.ink };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isReadOnly}
      className={cn(
        'relative flex min-h-[48px] w-full items-center gap-2 rounded-[4px] border-[1.5px] px-2.5 py-2.5 text-left transition sm:px-3.5',
        'focus:outline-none focus-visible:ring-2',
        isSelected && 'ring-2 ring-[#F2A0C4]/50',
        !isReadOnly && !grade && 'cursor-pointer hover:border-[#F2A0C4] active:scale-[0.99]',
        isReadOnly && 'cursor-default',
      )}
      style={cardStyle}
      aria-label={`${type === 'prompt' ? 'Prompt' : 'Option'}: ${label}`}
      aria-pressed={isSelected || isMatched}
    >
      {/* Match number badge — pair color before grading, good/red after */}
      {matchNumber !== null && (
        <span
          className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white sm:h-6 sm:w-6 sm:text-xs"
          style={{
            fontFamily: zineFonts.cond,
            background:
              grade === 'perfect'
                ? zineColors.good
                : grade === 'wrong'
                  ? zineColors.redApplied
                  : pairColor?.solid ?? zineColors.redApplied,
          }}
        >
          {matchNumber}
        </span>
      )}

      {/* Evaluation icons */}
      {grade === 'perfect' && (
        <span
          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full"
          style={{ background: zineColors.good }}
        >
          <CheckCircle2 className="h-4 w-4 text-white" />
        </span>
      )}
      {grade === 'wrong' && (
        <span
          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full"
          style={{ background: zineColors.redApplied }}
        >
          <AlertCircle className="h-4 w-4 text-white" />
        </span>
      )}

      {/* Link icon for matched items */}
      {isMatched && !grade && (
        <span
          className="hidden flex-shrink-0 sm:block"
          style={{ color: pairColor?.solid ?? zineColors.redApplied }}
        >
          <Link2 className="h-4 w-4" />
        </span>
      )}

      {/* Content */}
      <span className="flex-1 break-words text-xs font-medium leading-snug sm:text-sm">
        {renderInlineMarkdown(label, `${type}-${id}`)}
      </span>
    </button>
  );
}

export function MatchingExercise({
  content,
  onExerciseSubmit,
  feedback,
  variant = 'card',
  isReadOnly = false,
}: MatchingExerciseProps) {
  void variant; // the workbook panel is the single presentation for this exercise
  const { t } = useTranslation('p2e');
  const pairs: MatchingPair[] = useMemo(() => {
    const rawPairs = content.pairs ?? [];
    return rawPairs.map((pair: any, index: number) => ({
      pairId: pair.pairId ?? `pair-${index}`,
      promptId: pair.promptId ?? pair.pairId ?? `prompt-${index}`,
      promptLabel: pair.promptLabel ?? '',
      optionId: pair.optionId ?? `option-${index}`,
      optionLabel: pair.optionLabel ?? '',
      order: pair.order ?? index,
    }));
  }, [content.pairs]);

  const lastSubmission = useMemo(() => {
    const submission = content.lastSubmission;
    if (submission?.type === ExerciseType.Matching) {
      return submission;
    }
    return null;
  }, [content.lastSubmission]);

  const lastEvaluationDetails = useMemo(() => {
    const evaluation = (content.lastEvaluation as any) ?? null;
    const autoDetails = (evaluation?.auto as any)?.details ?? evaluation?.details;
    return autoDetails?.kind === 'Matching' ? autoDetails : null;
  }, [content.lastEvaluation]);

  // Build sets of valid IDs for quick lookup
  const validPromptIds = useMemo(() => new Set(pairs.map((p) => p.promptId)), [pairs]);
  const validOptionIds = useMemo(() => new Set(pairs.map((p) => p.optionId)), [pairs]);

  // Map promptId to its index (kept for the MatchState shape / submission payload)
  const promptIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    pairs.forEach((pair, index) => {
      map.set(pair.promptId, index);
    });
    return map;
  }, [pairs]);

  const getColorIndexForPrompt = useCallback(
    (promptId: string) => promptIndexMap.get(promptId) ?? 0,
    [promptIndexMap]
  );

  // Initialize matches from last submission, filtering out stale IDs that no longer exist
  const [matches, setMatches] = useState<MatchState[]>(() => {
    if (lastSubmission?.matches) {
      // Filter out matches that reference IDs no longer in the current exercise definition
      return lastSubmission.matches
        .filter((m: any) =>
          validPromptIds.has(m.promptId) && validOptionIds.has(m.selectedOptionId)
        )
        .map((m: any) => {
          const promptIndex = pairs.findIndex((p) => p.promptId === m.promptId);
          return {
            promptId: m.promptId,
            optionId: m.selectedOptionId,
            colorIndex: promptIndex >= 0 ? promptIndex : 0,
          };
        });
    }
    return [];
  });

  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Track if user has modified matches since last evaluation (to hide evaluation feedback)
  const [hasModifiedSinceEvaluation, setHasModifiedSinceEvaluation] = useState(false);

  const shuffledOptions = useMemo(() => {
    const options = pairs.map((pair) => ({
      optionId: pair.optionId,
      optionLabel: pair.optionLabel,
      correctPromptId: pair.promptId,
    }));
    const seed = (content.id ?? 'default').split('').reduce(
      (acc, char) => acc + char.charCodeAt(0),
      0
    );
    return seededShuffle(options, seed);
  }, [pairs, content.id]);

  // Build evaluation map - only show if user hasn't modified since evaluation
  // Also filter out evaluations for IDs that no longer exist in the current exercise
  const evaluationMap = useMemo(() => {
    // Don't show evaluation if user has modified their matches
    if (hasModifiedSinceEvaluation || !lastEvaluationDetails?.pairEvaluations) {
      return new Map<string, { isCorrect: boolean; grade?: string }>();
    }

    const map = new Map<string, { isCorrect: boolean; grade?: string }>();
    for (const evaluation of lastEvaluationDetails.pairEvaluations) {
      // Only include evaluations for IDs that still exist in the current exercise
      if (evaluation.selectedOptionId && validOptionIds.has(evaluation.selectedOptionId)) {
        map.set(evaluation.selectedOptionId, {
          isCorrect: evaluation.isCorrect,
          grade: evaluation.isCorrect ? 'perfect' : 'wrong',
        });
      }
      if (evaluation.promptId && validPromptIds.has(evaluation.promptId)) {
        map.set(evaluation.promptId, {
          isCorrect: evaluation.isCorrect,
          grade: evaluation.isCorrect ? 'perfect' : 'wrong',
        });
      }
    }
    return map;
  }, [lastEvaluationDetails, hasModifiedSinceEvaluation, validPromptIds, validOptionIds]);

  // Get match info for an item
  const getMatchInfo = useCallback(
    (id: string, type: 'prompt' | 'option') => {
      const match = matches.find((m) =>
        type === 'prompt' ? m.promptId === id : m.optionId === id
      );
      if (!match) return { isMatched: false, matchNumber: null, pairColor: null };

      const matchIndex = matches.indexOf(match);
      return {
        isMatched: true,
        matchNumber: matchIndex + 1,
        // Keyed to the prompt's position so the hue survives re-ordering
        // and both cards of the pair always share it.
        pairColor: zinePairColorFor(match.colorIndex),
      };
    },
    [matches]
  );

  const handleCardClick = useCallback(
    (id: string, type: 'prompt' | 'option') => {
      if (isReadOnly) return;

      if (lastEvaluationDetails && !hasModifiedSinceEvaluation) {
        setHasModifiedSinceEvaluation(true);
      }

      const isPrompt = type === 'prompt';
      const matchKey = isPrompt ? 'promptId' : 'optionId';
      const counterpartSelected = isPrompt ? selectedOptionId : selectedPromptId;

      // If already matched, unmatch it
      const existingMatch = matches.find((m) => m[matchKey] === id);
      if (existingMatch) {
        setMatches((prev) => prev.filter((m) => m[matchKey] !== id));
        return;
      }

      // If counterpart is selected, create a match
      if (counterpartSelected) {
        const counterpartKey = isPrompt ? 'optionId' : 'promptId';
        const alreadyMatched = matches.find((m) => m[counterpartKey] === counterpartSelected);
        if (!alreadyMatched) {
          const promptId = isPrompt ? id : counterpartSelected;
          const optionId = isPrompt ? counterpartSelected : id;
          setMatches((prev) => [
            ...prev,
            { promptId, optionId, colorIndex: getColorIndexForPrompt(promptId) },
          ]);
        }
        setSelectedPromptId(null);
        setSelectedOptionId(null);
        return;
      }

      // Toggle selection
      if (isPrompt) {
        setSelectedPromptId((prev) => (prev === id ? null : id));
        setSelectedOptionId(null);
      } else {
        setSelectedOptionId((prev) => (prev === id ? null : id));
        setSelectedPromptId(null);
      }
    },
    [isReadOnly, matches, selectedPromptId, selectedOptionId, getColorIndexForPrompt, lastEvaluationDetails, hasModifiedSinceEvaluation]
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!onExerciseSubmit) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Build matches based on current state
      const matchesSubmission = pairs.map((pair) => {
        const match = matches.find((m) => m.promptId === pair.promptId);
        return {
          promptId: pair.promptId,
          selectedOptionId: match?.optionId ?? '',
        };
      });

      await onExerciseSubmit(content, {
        type: ExerciseType.Matching,
        matches: matchesSubmission,
      });

      // Reset modification flag so new evaluation results will show
      setHasModifiedSinceEvaluation(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = matches.length > 0;
  const allMatched = matches.length === pairs.length;

  const { verdictMessage, extraMessages } = splitWorkbookFeedback(feedback);

  const columnHeadingStyle = {
    fontFamily: zineFonts.cond,
    color: zineColors.warmGray,
  } as const;

  return (
    <form onSubmit={handleSubmit}>
      <WorkbookPanel
        tab={t('zine.workbook.tab.autoChecked')}
        title={content.title ? renderInlineMarkdown(content.title, 'matching-title') : undefined}
        hint={t('zine.workbook.matchInstruction')}
        footer={
          <>
            {onExerciseSubmit && !isReadOnly && (
              <ZineButton type="submit" isLoading={isSubmitting} disabled={!canSubmit || isSubmitting}>
                {isSubmitting
                  ? t('zine.workbook.submitting')
                  : allMatched
                    ? t('zine.workbook.checkAnswers')
                    : t('zine.workbook.submitMatchCount', { count: matches.length })}
              </ZineButton>
            )}
            {!isReadOnly && <WorkbookAttemptText content={content} />}
            <WorkbookVerdict pending={isSubmitting} message={verdictMessage} />
            <WorkbookExtraFeedback pending={isSubmitting} messages={extraMessages} />
          </>
        }
      >
        <ContentDescription text={content.description} markdown className="mb-3" />

        {/* Matching grid */}
        <div className="mx-auto mb-2 grid max-w-2xl grid-cols-2 gap-2 sm:gap-4">
          {/* Prompts Column */}
          <div className="space-y-2 sm:space-y-3">
            <h4
              className="mb-2 text-center text-[.68rem] font-semibold uppercase tracking-[.18em] sm:mb-3"
              style={columnHeadingStyle}
            >
              {t('zine.workbook.leftColumn')}
            </h4>
            {pairs.map((pair) => {
              const matchInfo = getMatchInfo(pair.promptId, 'prompt');
              const evaluation = evaluationMap.get(pair.promptId);

              return (
                <MatchCard
                  key={pair.promptId}
                  id={pair.promptId}
                  label={pair.promptLabel}
                  isSelected={selectedPromptId === pair.promptId}
                  isMatched={matchInfo.isMatched}
                  matchNumber={matchInfo.matchNumber}
                  pairColor={matchInfo.pairColor}
                  evaluation={evaluation as any}
                  isReadOnly={isReadOnly}
                  onClick={() => handleCardClick(pair.promptId, 'prompt')}
                  type="prompt"
                />
              );
            })}
          </div>

          {/* Options Column */}
          <div className="space-y-2 sm:space-y-3">
            <h4
              className="mb-2 text-center text-[.68rem] font-semibold uppercase tracking-[.18em] sm:mb-3"
              style={columnHeadingStyle}
            >
              {t('zine.workbook.rightColumn')}
            </h4>
            {shuffledOptions.map((option) => {
              const matchInfo = getMatchInfo(option.optionId, 'option');
              const evaluation = evaluationMap.get(option.optionId);

              return (
                <MatchCard
                  key={option.optionId}
                  id={option.optionId}
                  label={option.optionLabel}
                  isSelected={selectedOptionId === option.optionId}
                  isMatched={matchInfo.isMatched}
                  matchNumber={matchInfo.matchNumber}
                  pairColor={matchInfo.pairColor}
                  evaluation={evaluation as any}
                  isReadOnly={isReadOnly}
                  onClick={() => handleCardClick(option.optionId, 'option')}
                  type="option"
                />
              );
            })}
          </div>
        </div>

        {/* Progress line */}
        {!isReadOnly && matches.length > 0 && (
          <p
            className="mb-3 text-center text-[.74rem]"
            style={{ color: allMatched ? zineColors.good : zineColors.warmGray }}
          >
            {allMatched
              ? t('zine.workbook.readyToSubmit')
              : t('zine.workbook.matchedCount', { matched: matches.length, total: pairs.length })}
          </p>
        )}

        {/* Match summary chips (hidden on mobile for space) */}
        {matches.length > 0 && (!lastEvaluationDetails || hasModifiedSinceEvaluation) && (
          <div className="mb-3 hidden flex-wrap justify-center gap-2 sm:flex">
            {matches.map((match, index) => {
              const prompt = pairs.find((p) => p.promptId === match.promptId);
              const option = shuffledOptions.find((o) => o.optionId === match.optionId);

              if (!prompt || !option) return null;

              const chipColor = zinePairColorFor(match.colorIndex);
              return (
                <div
                  key={`match-${index}`}
                  className="flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium"
                  style={{
                    background: chipColor.soft,
                    borderColor: chipColor.solid,
                    color: zineColors.ink,
                  }}
                >
                  <span className="max-w-[120px] truncate">
                    {prompt.promptLabel}
                  </span>
                  <Link2 className="h-3 w-3 flex-shrink-0 opacity-60" />
                  <span className="max-w-[120px] truncate">
                    {option.optionLabel}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </WorkbookPanel>
    </form>
  );
}
